import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useContext, useEffect, useState } from "react";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import { setMe } from "@actions/sessionActions";
import BaseErrors from "@components/fields/BaseErrors";
import styles from "@components/forms/EmailSecurityForm.module.css";
import Form from "@components/forms/Form";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import { sessionContext } from "@context/Contexts";
import useMyClient from "@hooks/useMyClient";
import {
  ClientUpdateDocument,
  ClientUpdateMutation,
  ClientUpdateMutationVariables,
  EmailFrequencyEnum,
  EmailSecurityEnum,
  GetClientWithAccountQuery
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";

type FormData = {
  emailFrequency: EmailFrequencyEnum;
  emailSecurity: EmailSecurityEnum;
};

type Props = {
  client: GetClientWithAccountQuery["client"];
};

type EmailFrequencyControlsProps = {
  form: UseFormReturn<FormData, any, undefined>;
};

const EmailFrequencyControls = ({ form }: EmailFrequencyControlsProps) => {
  const emailFrequencyOptions = [EmailFrequencyEnum.EACH_MESSAGE, EmailFrequencyEnum.DAILY_DIGEST];

  return (
    <Controller
      control={form.control}
      name="emailFrequency"
      render={({ field: { onChange, value } }) => (
        <div className={styles.frequencyOptions}>
          {emailFrequencyOptions.map(option => (
            <div className={styles.option} key={option}>
              <ButtonLink data-test={`frequency-radio-${option}`} onClick={() => onChange(option)} stylized={false}>
                <div className={styles.frequencyOptionRow}>
                  <Icon
                    color={value === option ? "primary" : "gray-500"}
                    icon={value === option ? "circleDot" : "circle"}
                  />
                  <BodyCopy color="gray-500">
                    <FormattedMessage id={`models.account.emailFrequency.title.${option}`} />
                  </BodyCopy>
                </div>
              </ButtonLink>
            </div>
          ))}
        </div>
      )}
    />
  );
};

const EmailSecurityForm = ({ client }: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { dispatch } = useContext(sessionContext);

  const [clientUpdate] = useMutation<ClientUpdateMutation, ClientUpdateMutationVariables>(ClientUpdateDocument);
  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      emailFrequency: yup.string().oneOf(Object.values(EmailFrequencyEnum)).defined(),
      emailSecurity: yup.string().oneOf(Object.values(EmailSecurityEnum)).defined()
    })
    .required();

  const defaultValues = {
    emailFrequency: client.emailFrequency,
    emailSecurity: client.emailSecurity
  };

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const watchEmailSecurity = form.watch("emailSecurity");
  const watchEmailFrequency = form.watch("emailFrequency");

  const handleChange = useCallback(async () => {
    setBaseErrors([]);
    return clientUpdate({
      variables: {
        id: client.id,
        ...form.getValues()
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.clientUpdate,
        intl,
        onSuccess: () => {
          if (response.data?.clientUpdate?.client) {
            if (myClient && response.data.clientUpdate.client.id === myClient?.id) {
              dispatch(setMe(response.data.clientUpdate.client));
            }
            setSubmitting(false);
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.clientAccountUpdate"
      });
    });
  }, [client.id, clientUpdate, dispatch, form, intl, myClient]);

  useEffect(() => {
    if (!submitting && (watchEmailSecurity !== client.emailSecurity || watchEmailFrequency !== client.emailFrequency)) {
      setSubmitting(true);
      handleChange();
    }
  }, [client.emailFrequency, client.emailSecurity, handleChange, submitting, watchEmailFrequency, watchEmailSecurity]);

  const emailSecurityOptions = [EmailSecurityEnum.FULL, EmailSecurityEnum.SNIPPET, EmailSecurityEnum.PRIVATE];

  return (
    <Form data-test="email-security-form" hookForm={form}>
      <BaseErrors errors={baseErrors} />
      <Controller
        control={form.control}
        name="emailSecurity"
        render={({ field: { onChange, value } }) => (
          <div className={styles.securityOptions}>
            {emailSecurityOptions.map(option => (
              <div className={styles.option} key={option}>
                <div className={styles.securityOptionRow}>
                  <ButtonLink
                    aria-label={intl.formatMessage({ id: `models.account.emailSecurity.title.${option}` })}
                    onClick={() => onChange(option)}
                    stylized={false}
                  >
                    <Icon
                      color={value === option ? "primary" : "gray-500"}
                      icon={value === option ? "circleDot" : "circle"}
                    />
                  </ButtonLink>
                  <div className={styles.securityOptionColumn}>
                    <BodyCopy bold color="primary">
                      <FormattedMessage id={`models.account.emailSecurity.title.${option}`} />
                    </BodyCopy>
                    <BodyCopy color="gray-500">
                      <FormattedMessage id={`models.account.emailSecurity.description.${option}`} />
                    </BodyCopy>
                    <div className={option === value ? "" : "ion-hide"}>
                      <EmailFrequencyControls form={form} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      />
    </Form>
  );
};

export default EmailSecurityForm;
